import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { onAuthStateChanged } from "firebase/auth";
import { addDoc, collection, doc, getDoc, getDocs, orderBy, query, where } from "firebase/firestore";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import RelatorioRupturaTijoloSoloCimento from "./RelatorioRupturaTijoloSoloCimento";
import { calcularBlocos, criarBlocos, criarBlocosAbsorcao, formatarDataBR } from "./tijoloSoloCimentoCalculos";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const CONFIG_TIJOLO_SOLO_CIMENTO = {
  colecao: "tblEnsaiosSoloCimento",
  quantidadeCompressao: 7,
  quantidadeAbsorcao: 3,
  chaveSequencial: "ensaio_solo_cimento",
  templateCodigo: "RE-SC-0{numero}/{ano}",
  regexCodigo: /RE-SC-0(\d+)\/\d{4}/,
  rotaDetalhes: "/app/ensaio-solo-cimento",
  titulo: "Ensaio de Tijolo Solo-Cimento",
  tituloNovo: "Novo Ensaio de Tijolo Solo-Cimento",
  tituloEditar: "Editar Ensaio de Tijolo Solo-Cimento",
  rotuloUnidades: "Tijolos",
  tipoSoloCimento: true,
};

const estadoInicial = {
  codigo: "",
  dataRelatorio: "",
  cliente: "",
  clienteId: "",
  empresaSolicitante: "",
  obra: "",
  obraId: "",
  endereco: "",
  localAplicacao: "",
  procedencia: "",
  blocoDescricao: "",
  lote: "",
  dimensoesDeclaradas: "",
  resistenciaEsperada: "",
  dataFabricacao: "",
  dataFabricacaoNaoInformada: false,
  dataRecebimento: "",
  dataEnsaio: "",
  quantidadeAmostras: "",
  observacao: "",
  prensaRuptura: null,
  relatorioFotografico: [],
  absorcaoFotos: [],
  blocos: [],
  blocosAbsorcao: [],
};

function calcularProximoCodigo(lista) {
  const ano = new Date().getFullYear();
  const numeros = lista
    .map((item) => String(item.codigo || "").match(/RE-BLC-0(\d+)\/\d{4}/))
    .filter(Boolean)
    .map((match) => Number(match[1]))
    .filter(Number.isFinite);
  const proximo = numeros.length ? Math.max(...numeros) + 1 : lista.length + 1;
  return `RE-BLC-0${String(proximo).padStart(3, "0")}/${ano}`;
}

function normalizarTexto(valor) {
  return String(valor || "").toLowerCase();
}

function extrairValorResistencia(valor) {
  return String(valor || "").replace(/[^\d,.]/g, "");
}

function formatarResistenciaEsperada(valor) {
  const numero = extrairValorResistencia(valor).trim();
  return numero ? `${numero} MPa` : "";
}

function prepararDados(formulario) {
  return {
    ...formulario,
    resistenciaEsperada: formatarResistenciaEsperada(formulario.resistenciaEsperada),
  };
}


export default function CadastroRupturaTijoloSoloCimento({ configuracao = CONFIG_TIJOLO_SOLO_CIMENTO }) {
  const COLLECTION = configuracao.colecao;
  const QUANTIDADE_BLOCOS_PADRAO = configuracao.quantidadeCompressao;
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const [usuario, setUsuario] = useState(null);
  const [ensaios, setEnsaios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [obrasSistema, setObrasSistema] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [form, setForm] = useState(estadoInicial);
  const [openDialog, setOpenDialog] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [ensaioEditando, setEnsaioEditando] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [modoPrint, setModoPrint] = useState(false);
  const [ensaioPrint, setEnsaioPrint] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUsuario(null);
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      setUsuario(snap.exists() ? { uid: user.uid, ...snap.data() } : { uid: user.uid });
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    carregarEnsaios();
    carregarClientes();
  }, []);

  async function carregarEnsaios() {
    setLoading(true);
    const q = query(collection(db, COLLECTION), orderBy("criadoEm", "desc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setEnsaios(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
    setLoading(false);
  }

  async function carregarClientes() {
    const q = query(collection(db, "clientes"), orderBy("nome", "asc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setClientes(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
  }

  async function carregarObrasPorCliente(clienteId) {
    if (!clienteId) {
      setObrasSistema([]);
      return;
    }
    const q = query(collection(db, "obras"), where("clienteId", "==", clienteId), orderBy("nome", "asc"));
    const snap = await getDocs(consultaTenantAtual(q));
    setObrasSistema(snap.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
  }

  function abrirNovoCadastro() {
    setModoEdicao(false);
    setEnsaioEditando(null);
    setClienteSelecionado(null);
    setObrasSistema([]);
    setForm({
      ...estadoInicial,
      blocos: criarBlocos(QUANTIDADE_BLOCOS_PADRAO),
      blocosAbsorcao: criarBlocosAbsorcao(configuracao.quantidadeAbsorcao),
      quantidadeAmostras: QUANTIDADE_BLOCOS_PADRAO,
    });
    setOpenDialog(true);
  }

  async function editarEnsaio(ensaio) {
    setModoEdicao(true);
    setEnsaioEditando(ensaio);
    const clienteEncontrado = clientes.find((cliente) => cliente.id === ensaio.clienteId);
    setClienteSelecionado(clienteEncontrado || null);
    if (ensaio.clienteId) await carregarObrasPorCliente(ensaio.clienteId);
    setForm({
      ...estadoInicial,
      ...ensaio,
      quantidadeAmostras: QUANTIDADE_BLOCOS_PADRAO,
      blocos: ensaio.blocos?.length ? ensaio.blocos.slice(0, QUANTIDADE_BLOCOS_PADRAO) : criarBlocos(QUANTIDADE_BLOCOS_PADRAO),
    });
    setOpenDialog(true);
  }

 function selecionarObra(obraId) {
  const obraSelecionada = obrasSistema.find((obra) => obra.id === obraId);
  setForm({
    ...form,
    obra: obraSelecionada?.nome || "",
    obraId: obraSelecionada?.id || "",
    endereco: obraSelecionada?.endereco || "",
    localAplicacao: obraSelecionada?.nome || "",
    empresaSolicitante: obraSelecionada?.nome || "",
  });
}

  async function salvarEnsaio() {
    if (!form.clienteId) {
      alert("Selecione o cliente antes de salvar.");
      return;
    }

    const quantidade = QUANTIDADE_BLOCOS_PADRAO;
    const blocosAtuais = Array.isArray(form.blocos) ? form.blocos : [];
    const blocos = blocosAtuais.length ? blocosAtuais.slice(0, quantidade) : criarBlocos(quantidade);
    const codigo = modoEdicao
      ? form.codigo
      : form.codigo || (await gerarSequencial({
          chave: configuracao.chaveSequencial,
          escopo: new Date(form.dataRelatorio || new Date()).getFullYear(),
          formato: {
            padding: 3,
            ano: new Date(form.dataRelatorio || new Date()).getFullYear(),
            template: configuracao.templateCodigo,
          },
          valorMinimo: obterMaiorSequencial(ensaios, configuracao.regexCodigo),
        })).codigo;
    const dados = {
      ...prepararDados(form),
      codigo,
      empresaSolicitante: form.obra || form.empresaSolicitante || form.cliente,
      procedencia: form.procedencia || form.cliente,
      quantidadeAmostras: quantidade,
      blocos: calcularBlocos(blocos, { dividirComprimentoArea: configuracao.tipoSoloCimento }),
      blocosAbsorcao: form.blocosAbsorcao?.length ? form.blocosAbsorcao : criarBlocosAbsorcao(configuracao.quantidadeAbsorcao),
      atualizadoEm: new Date(),
      atualizadoPor: usuario?.uid || null,
    };

    if (modoEdicao && ensaioEditando?.id) {
      await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, COLLECTION, ensaioEditando.id),
        original: ensaioEditando,
        atual: { ...ensaioEditando, ...dados },
        metadados: { atualizadoEm: dados.atualizadoEm, atualizadoPor: dados.atualizadoPor },
      });
    } else {
      await addDoc(collection(db, COLLECTION), aplicarTenantAtual({
        ...dados,
        criadoEm: new Date(),
        criadoPor: usuario?.uid || null,
      }));
    }

    setOpenDialog(false);
    await carregarEnsaios();
  }

  function abrirRelatorio(ensaio, imprimir = false) {
    setEnsaioPrint(ensaio);
    setAutoPrint(imprimir);
    setModoPrint(true);
  }

  const ensaiosFiltrados = useMemo(() => {
    const termo = normalizarTexto(filtro);
    if (!termo) return ensaios;
    return ensaios.filter((item) =>
      [item.codigo, item.cliente, item.obra, item.localAplicacao, item.dataEnsaio].some((campo) =>
        contemTextoPesquisa(normalizarTexto(campo), termo)
      )
    );
  }, [ensaios, filtro]);

  if (modoPrint && ensaioPrint) {
    return (
      <RelatorioRupturaTijoloSoloCimento
        ensaio={ensaioPrint}
        fotosRelatorio={(ensaioPrint.relatorioFotografico || []).filter((foto) => foto.url)}
        autoPrint={autoPrint}
        onVoltar={() => {
          setModoPrint(false);
          setAutoPrint(false);
          setEnsaioPrint(null);
        }}
      />
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>{configuracao.titulo}</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Lista de ensaios cadastrados</Typography>
        <Stack direction={{ xs: "column", md: "row" }} alignItems="stretch" spacing={2} mb={3}>
          <Button variant="contained" onClick={abrirNovoCadastro}>+ Novo Ensaio</Button>
          <TextField size="small" label="Pesquisar" value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={{ flex: 1 }} />
          <TextField size="small" label="Qtd Ensaios" value={ensaiosFiltrados.length} InputProps={{ readOnly: true }} sx={{ width: { xs: "100%", md: 140 } }} />
        </Stack>
        </Paper>

        <Paper sx={{ borderRadius: "12px", overflow: "auto", border: "1px solid #e2e8f0", boxShadow: "none" }}>
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 900 }}>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Código</strong></TableCell>
                <TableCell>Cliente / Obra</TableCell>
                <TableCell>Data ensaio</TableCell>
                <TableCell>Local aplicação</TableCell>
                <TableCell align="center">{configuracao.rotuloUnidades}</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ensaiosFiltrados.map((item) => (
                <TableRow key={item.id} hover sx={{ height: 48, cursor: "pointer" }} onClick={() => history.push(`${configuracao.rotaDetalhes}/${item.id}`)}>
                  <TableCell>{item.codigo || "-"}</TableCell>
                  <TableCell>
                    <div className="lista-dado-principal">{item.cliente || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {item.obra || "-"}</div>
                  </TableCell>
                  <TableCell className="lista-dado-secundario">{formatarDataBR(item.dataEnsaio)}</TableCell>
                  <TableCell className="lista-dado-destaque">{item.localAplicacao || "-"}</TableCell>
                  <TableCell align="center">{item.quantidadeAmostras || item.blocos?.length || 0}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Visualizar">
                      <IconButton size="small" onClick={(event) => { event.stopPropagation(); abrirRelatorio(item, false); }}>
                        <VisibilityIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar cadastro">
                      <IconButton size="small" onClick={(event) => { event.stopPropagation(); editarEnsaio(item); }}>
                        <EditIcon sx={{ color: "#0288d1" }} />
                      </IconButton>
                    </Tooltip>
                    <HistoricoCadastro colecao={COLLECTION} documentoId={item.id} registro={item} />
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRow><TableCell colSpan={6}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!loading && ensaiosFiltrados.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">Nenhum ensaio cadastrado.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={() => !salvandoCadastro && setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>{modoEdicao ? configuracao.tituloEditar : configuracao.tituloNovo}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Código" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} fullWidth />
            <Autocomplete
              options={clientes}
              getOptionLabel={(option) => option?.nome || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={clienteSelecionado}
              onChange={(event, newValue) => {
                setClienteSelecionado(newValue);
                setForm({
                  ...form,
                  cliente: newValue?.nome || "",
                  clienteId: newValue?.id || "",
                  empresaSolicitante: newValue?.nome || "",
                  procedencia: newValue?.nome || "",
                  obra: "",
                  obraId: "",
                  endereco: "",
                  localAplicacao: "",
                });
                carregarObrasPorCliente(newValue?.id);
              }}
              renderInput={(params) => <TextField {...params} label="Cliente" placeholder="Pesquisar cliente..." fullWidth />}
            />
            <TextField select label="Empreendimento/Obra" value={form.obraId || ""} onChange={(e) => selecionarObra(e.target.value)} fullWidth disabled={!clienteSelecionado}>
              {obrasSistema.map((obra) => <MenuItem key={obra.id} value={obra.id}>{obra.nome}</MenuItem>)}
            </TextField>
            <TextField label="Data do relatório" type="date" InputLabelProps={{ shrink: true }} value={form.dataRelatorio} onChange={(e) => setForm({ ...form, dataRelatorio: e.target.value })} fullWidth />
            {configuracao.tipoSoloCimento && <TextField label="Data do recebimento" type="date" InputLabelProps={{ shrink: true }} value={form.dataRecebimento || ""} onChange={(e) => setForm({ ...form, dataRecebimento: e.target.value })} fullWidth />}
            <TextField label="Data do ensaio" type="date" InputLabelProps={{ shrink: true }} value={form.dataEnsaio} onChange={(e) => setForm({ ...form, dataEnsaio: e.target.value })} fullWidth />
            {configuracao.tipoSoloCimento && <TextField label="Lote" value={form.lote || ""} onChange={(e) => setForm({ ...form, lote: e.target.value })} fullWidth />}
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr auto" }} gap={2} alignItems="center">
              <TextField
                label={configuracao.tipoSoloCimento ? "Data de fabricação" : "Data de fabricação/lote"}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.dataFabricacao}
                onChange={(e) => setForm({
                  ...form,
                  dataFabricacao: e.target.value,
                  lote: configuracao.tipoSoloCimento ? form.lote : e.target.value,
                  dataFabricacaoNaoInformada: false,
                })}
                fullWidth
                disabled={form.dataFabricacaoNaoInformada}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.dataFabricacaoNaoInformada)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        dataFabricacaoNaoInformada: e.target.checked,
                        dataFabricacao: e.target.checked ? "" : form.dataFabricacao,
                        lote: configuracao.tipoSoloCimento ? form.lote : e.target.checked ? "" : form.lote,
                      })
                    }
                  />
                }
                label="Não Informado"
              />
            </Box>
            <TextField label={`Quantidade de ${configuracao.rotuloUnidades.toLowerCase()}`} type="number" value={QUANTIDADE_BLOCOS_PADRAO} InputProps={{ readOnly: true }} fullWidth />
            <TextField label="Local de aplicação" value={form.localAplicacao} onChange={(e) => setForm({ ...form, localAplicacao: e.target.value })} fullWidth />
            <TextField label={configuracao.tipoSoloCimento ? "Tijolo" : "Bloco"} value={form.blocoDescricao} onChange={(e) => setForm({ ...form, blocoDescricao: e.target.value })} fullWidth />
            <TextField label="Dimensões declaradas" value={form.dimensoesDeclaradas} onChange={(e) => setForm({ ...form, dimensoesDeclaradas: e.target.value })} fullWidth />
            <TextField label="Resistência esperada (MPa)" value={extrairValorResistencia(form.resistenciaEsperada)} onChange={(e) => setForm({ ...form, resistenciaEsperada: extrairValorResistencia(e.target.value) })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={salvandoCadastro}>Cancelar</Button>
          <Button variant="contained" onClick={() => executarSalvamento(salvarEnsaio)} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
